import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetExchangeRateQuery,
  useUpdateExchangeRateMutation,
  useGetAllProfitMarginsQuery,
  useUpdateServiceProfitMarginMutation,
  ServiceName,
  useGetCashbackSettingsQuery,
  useUpdateCashbackSettingsMutation,
  useToggleCashbackSystemMutation,
  CashbackServiceName,
  useGetSystemWebhookSecretQuery,
  useUpdateSystemWebhookSecretMutation,
} from '../../store/api/adminApi';

export function AdminSystemConfigPage() {
  const { t } = useTranslation();
  const [exchangeRate, setExchangeRate] = useState('');
  const [isEditingExchangeRate, setIsEditingExchangeRate] = useState(false);

  // Exchange Rate
  const { data: exchangeRateData, isLoading: exchangeRateLoading } = useGetExchangeRateQuery();
  const [updateExchangeRate, { isLoading: isUpdatingExchangeRate }] = useUpdateExchangeRateMutation();

  // Profit Margins
  const { data: profitMarginsData, isLoading: profitMarginsLoading } = useGetAllProfitMarginsQuery();
  const [updateServiceProfitMargin, { isLoading: isUpdatingMargin }] = useUpdateServiceProfitMarginMutation();
  const [editingService, setEditingService] = useState<ServiceName | null>(null);
  const [marginForms, setMarginForms] = useState<Record<string, Record<string, string>>>({});

  // Cashback Settings
  const { data: cashbackData, isLoading: cashbackLoading } = useGetCashbackSettingsQuery();
  const [updateCashbackSettings, { isLoading: isUpdatingCashback }] = useUpdateCashbackSettingsMutation();
  const [toggleCashbackSystem, { isLoading: isTogglingCashback }] = useToggleCashbackSystemMutation();
  // Note: updateServiceCashback and updateProxyCheapCashback are available but not used in this component
  // They are kept for potential future use

  const [isEditingCashback, setIsEditingCashback] = useState(false);

  // Webhook Secret
  const { data: webhookSecretData, isLoading: webhookSecretLoading } = useGetSystemWebhookSecretQuery();
  const [updateWebhookSecret, { isLoading: isUpdatingWebhookSecret }] = useUpdateSystemWebhookSecretMutation();
  const [webhookSecretInput, setWebhookSecretInput] = useState('');
  const [isEditingWebhookSecret, setIsEditingWebhookSecret] = useState(false);

  const handleUpdateWebhookSecret = async () => {
    try {
      await updateWebhookSecret({ secret: webhookSecretInput }).unwrap();
      toast.success(webhookSecretInput
        ? 'Webhook secret configured successfully'
        : 'Webhook secret cleared'
      );
      setIsEditingWebhookSecret(false);
      setWebhookSecretInput('');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || 'Failed to update webhook secret');
    }
  };

  // Initialize exchange rate form
  useEffect(() => {
    if (exchangeRateData?.data.rate && !exchangeRate && !isEditingExchangeRate) {
      setExchangeRate(exchangeRateData.data.rate.usdToSypExchangeRate.toString());
    }
  }, [exchangeRateData, exchangeRate, isEditingExchangeRate]);

  const handleUpdateExchangeRate = async () => {
    const rate = parseFloat(exchangeRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error(t('admin.systemConfig.exchangeRate.invalidRate'));
      return;
    }

    try {
      await updateExchangeRate({ usdToSypExchangeRate: rate }).unwrap();
      toast.success(t('admin.systemConfig.exchangeRate.updateSuccess'));
      setIsEditingExchangeRate(false);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.exchangeRate.updateError')
      );
    }
  };
  const [cashbackForm, setCashbackForm] = useState({
    defaultPercentage: '',
    services: {} as Record<string, string>,
    proxycheap: {
      basic: '',
      dedicated: '',
      premium: '',
    },
  });

  const handleStartEditService = (serviceName: ServiceName, margins: Record<string, number>) => {
    setEditingService(serviceName);
    const formData: Record<string, string> = {};
    Object.entries(margins).forEach(([key, value]) => {
      // تجاهل الحقول apiKey, apiSecret, username, webhookSecret, webhookToken عند تحميل البيانات
      if (key === 'apiKey' || key === 'apiSecret' || key === 'username' || key === 'webhookSecret' || key === 'webhookToken') {
        return;
      }
      formData[key] = value.toString();
    });
    setMarginForms((prev) => ({ ...prev, [serviceName]: formData }));
  };

  const handleUpdateServiceMargin = async (serviceName: ServiceName) => {
    const formData = marginForms[serviceName];
    if (!formData) return;

    const updateData: Record<string, number> = {};
    let hasValidData = false;

    Object.entries(formData).forEach(([key, value]) => {
      // تجاهل الحقول apiKey, apiSecret, username, webhookSecret, webhookToken
      if (key === 'apiKey' || key === 'apiSecret' || key === 'username' || key === 'webhookSecret' || key === 'webhookToken') {
        return;
      }
      
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        updateData[key] = numValue;
        hasValidData = true;
      }
    });

    if (!hasValidData) {
      toast.error(t('admin.systemConfig.profitMargins.invalidMargin'));
      return;
    }

    try {
      await updateServiceProfitMargin({
        serviceName,
        data: updateData as Parameters<typeof updateServiceProfitMargin>[0]['data'],
      }).unwrap();
      toast.success(t('admin.systemConfig.profitMargins.updateSuccess'));
      setEditingService(null);
      setMarginForms((prev) => {
        const newForms = { ...prev };
        delete newForms[serviceName];
        return newForms;
      });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.profitMargins.updateError')
      );
    }
  };

  const handleStartEditCashback = () => {
    if (!cashbackData?.data.settings) return;
    const settings = cashbackData.data.settings;
    setCashbackForm({
      defaultPercentage: (settings.defaultPercentage * 100).toFixed(2),
      services: {
        smspool: settings.services.smspool ? (settings.services.smspool * 100).toFixed(2) : '',
        pvadeals: settings.services.pvadeals ? (settings.services.pvadeals * 100).toFixed(2) : '',
        durianRcs: settings.services.durianRcs ? (settings.services.durianRcs * 100).toFixed(2) : '',
        proxyline: settings.services.proxyline ? (settings.services.proxyline * 100).toFixed(2) : '',
        warehouse: settings.services.warehouse ? (settings.services.warehouse * 100).toFixed(2) : '',
      },
      proxycheap: {
        basic: settings.services.proxycheap?.basic ? (settings.services.proxycheap.basic * 100).toFixed(2) : '',
        dedicated: settings.services.proxycheap?.dedicated ? (settings.services.proxycheap.dedicated * 100).toFixed(2) : '',
        premium: settings.services.proxycheap?.premium ? (settings.services.proxycheap.premium * 100).toFixed(2) : '',
      },
    });
    setIsEditingCashback(true);
  };

  const handleUpdateCashback = async () => {
    const defaultPercentage = parseFloat(cashbackForm.defaultPercentage) / 100;
    if (isNaN(defaultPercentage) || defaultPercentage < 0 || defaultPercentage > 1) {
      toast.error(t('admin.systemConfig.cashback.invalidPercentage'));
      return;
    }

    const services: Record<string, number> = {};
    Object.entries(cashbackForm.services).forEach(([key, value]) => {
      if (value) {
        const numValue = parseFloat(value) / 100;
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
          // تقريب إلى 4 خانات عشرية لتجنب مشاكل الفاصلة العائمة
          services[key] = Math.round(numValue * 10000) / 10000;
        }
      }
    });

    const proxycheap: { basic?: number; dedicated?: number; premium?: number } = {};
    if (cashbackForm.proxycheap.basic) {
      const numValue = parseFloat(cashbackForm.proxycheap.basic) / 100;
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
        proxycheap.basic = Math.round(numValue * 10000) / 10000;
      }
    }
    if (cashbackForm.proxycheap.dedicated) {
      const numValue = parseFloat(cashbackForm.proxycheap.dedicated) / 100;
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
        proxycheap.dedicated = Math.round(numValue * 10000) / 10000;
      }
    }
    if (cashbackForm.proxycheap.premium) {
      const numValue = parseFloat(cashbackForm.proxycheap.premium) / 100;
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
        proxycheap.premium = Math.round(numValue * 10000) / 10000;
      }
    }

    if (Object.keys(proxycheap).length > 0) {
      services.proxycheap = proxycheap as any;
    }

    try {
      // تقريب النسبة الافتراضية أيضاً
      const roundedDefaultPercentage = Math.round(defaultPercentage * 10000) / 10000;
      
      await updateCashbackSettings({
        defaultPercentage: roundedDefaultPercentage,
        services: Object.keys(services).length > 0 ? services : undefined,
      }).unwrap();
      toast.success(t('admin.systemConfig.cashback.updateSuccess'));
      setIsEditingCashback(false);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.cashback.updateError')
      );
    }
  };

  const handleToggleCashback = async (isEnabled: boolean) => {
    try {
      await toggleCashbackSystem({ isEnabled }).unwrap();
      toast.success(
        isEnabled
          ? t('admin.systemConfig.cashback.enabledSuccess')
          : t('admin.systemConfig.cashback.disabledSuccess')
      );
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.systemConfig.cashback.toggleError')
      );
    }
  };

  const getServiceDisplayName = (serviceName: string): string => {
    const names: Record<string, string> = {
      smspool: 'SMSPool',
      pvadeals: 'PVA Deals',
      durianRcs: 'DurianRCS',
      proxylte: 'ProxyLTE',
      proxyline: 'ProxyLine',
      proxycheap: 'ProxyCheap',
      textverified: 'TextVerified',
      iproyal: 'ISP',
    };
    return names[serviceName] || serviceName;
  };

  const getCashbackServiceDisplayName = (serviceName: CashbackServiceName | 'proxycheap'): string => {
    const names: Record<string, string> = {
      smspool: 'SMSPool',
      pvadeals: 'PVA Deals',
      durianRcs: 'DurianRCS',
      proxyline: 'ProxyLine',
      warehouse: 'Warehouse',
      proxycheap: 'ProxyCheap',
    };
    return names[serviceName] || serviceName;
  };

  const getMarginFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      profitMargin: t('admin.systemConfig.profitMargins.fields.profitMargin'),
      profitMarginTemp: t('admin.systemConfig.profitMargins.fields.profitMarginTemp'),
      profitMarginPrem: t('admin.systemConfig.profitMargins.fields.profitMarginPrem'),
      profitMarginBase: t('admin.systemConfig.profitMargins.fields.profitMarginBase'),
      profitMarginDedicated: t('admin.systemConfig.profitMargins.fields.profitMarginDedicated'),
      profitMarginShared: t('admin.systemConfig.profitMargins.fields.profitMarginShared'),
      profitMarginPremium: t('admin.systemConfig.profitMargins.fields.profitMarginPremium'),
      profitMarginVoice: 'هامش الاتصال',
      profitMarginReuse: 'هامش إعادة الاستخدام',
    };
    return labels[fieldName] || fieldName;
  };

  if (exchangeRateLoading || profitMarginsLoading || cashbackLoading || webhookSecretLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const currentExchangeRate = exchangeRateData?.data.rate.usdToSypExchangeRate || 0;
  const profitMargins = profitMarginsData?.data.margins || {};
  const cashbackSettings = cashbackData?.data.settings;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.systemConfig.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.systemConfig.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Margins Section */}
        <div className="card-glass rounded-2xl shadow-2xl p-6">
          <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
            {t('admin.systemConfig.profitMargins.title')}
          </h2>

          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {Object.entries(profitMargins)
              .filter(([serviceName]) => !['one1store', 'proxy5g'].includes(serviceName))
              .map(([serviceName, margins]) => {
              const service = serviceName as ServiceName;
              const isEditing = editingService === service;
              const formData = marginForms[service] || {};

              return (
                <div
                  key={serviceName}
                  className="bg-neutral-dark/50 p-4 rounded-lg border border-neutral-dark"
                >
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-heading font-semibold text-neutral-light">
                      {getServiceDisplayName(service)}
                    </h3>
                    {!isEditing && (
                      <button
                        onClick={() => handleStartEditService(service, margins as Record<string, number>)}
                        className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                      >
                        {t('admin.systemConfig.profitMargins.edit')}
                      </button>
                    )}
                  </div>

                  {!isEditing ? (
                    <div className="space-y-2">
                      {Object.entries(margins as Record<string, number>)
                        .filter(([field]) => field !== 'apiKey' && field !== 'apiSecret' && field !== 'username' && field !== 'webhookSecret')
                        .map(([field, value]) => (
                          <div key={field} className="flex justify-between items-center">
                            <span className="text-sm text-neutral-medium font-body">
                              {getMarginFieldLabel(field)}:
                            </span>
                            <span className="text-sm font-heading font-semibold text-neutral-light">
                              {(value * 100).toFixed(2)}%
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Object.keys(margins as Record<string, number>)
                        .filter((field) => field !== 'apiKey' && field !== 'apiSecret' && field !== 'username' && field !== 'webhookSecret')
                        .map((field) => (
                          <div key={field}>
                            <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                              {getMarginFieldLabel(field)}
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={formData[field] || ''}
                              onChange={(e) => {
                                setMarginForms((prev) => ({
                                  ...prev,
                                  [service]: {
                                    ...prev[service],
                                    [field]: e.target.value,
                                  },
                                }));
                              }}
                              placeholder="0.00"
                              className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                            />
                          </div>
                        ))}
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => handleUpdateServiceMargin(service)}
                          disabled={isUpdatingMargin}
                          className="flex-1 px-3 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isUpdatingMargin ? (
                            <>
                              <ButtonSpinner size="xs" />
                              <span>{t('common.loading')}</span>
                            </>
                          ) : (
                            t('common.save')
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingService(null);
                            setMarginForms((prev) => {
                              const newForms = { ...prev };
                              delete newForms[service];
                              return newForms;
                            });
                          }}
                          className="flex-1 px-3 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 text-sm"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Cashback Settings & Exchange Rate Section */}
        <div className="card-glass rounded-2xl shadow-2xl p-6">
          {/* Exchange Rate Section */}
          <div className="mb-8 pb-8 border-b border-neutral-dark">
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.systemConfig.exchangeRate.title')}
            </h2>

            {!isEditingExchangeRate ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                    {t('admin.systemConfig.exchangeRate.currentRate')}
                  </label>
                  <p className="text-3xl font-heading font-bold text-brand-accent">
                    {currentExchangeRate.toLocaleString()} SYP
                  </p>
                  <p className="text-sm text-neutral-medium font-body mt-1">
                    {t('admin.systemConfig.exchangeRate.perUSD')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsEditingExchangeRate(true);
                    setExchangeRate(currentExchangeRate.toString());
                  }}
                  className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('admin.systemConfig.exchangeRate.edit')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                    {t('admin.systemConfig.exchangeRate.newRate')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    placeholder={t('admin.systemConfig.exchangeRate.placeholder')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleUpdateExchangeRate}
                    disabled={isUpdatingExchangeRate || !exchangeRate || parseFloat(exchangeRate) <= 0}
                    className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                  >
                    {isUpdatingExchangeRate ? (
                      <>
                        <ButtonSpinner size="xs" />
                        <span>{t('common.loading')}</span>
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingExchangeRate(false);
                      setExchangeRate('');
                    }}
                    className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Cashback Settings Section */}
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent">
                {t('admin.systemConfig.cashback.title')}
              </h2>
              {cashbackSettings && (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cashbackSettings.isEnabled}
                    onChange={(e) => handleToggleCashback(e.target.checked)}
                    disabled={isTogglingCashback}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-dark peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-dark after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-accent"></div>
                </label>
              )}
            </div>

          {!isEditingCashback ? (
            <div className="space-y-4">
              {cashbackSettings && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                      {t('admin.systemConfig.cashback.defaultPercentage')}
                    </label>
                    <p className="text-2xl font-heading font-bold text-brand-accent">
                      {(cashbackSettings.defaultPercentage * 100).toFixed(2)}%
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-heading font-semibold text-neutral-light">
                      {t('admin.systemConfig.cashback.services')}
                    </h3>
                    {Object.entries(cashbackSettings.services).map(([serviceName, value]) => {
                      if (serviceName === 'proxycheap' && typeof value === 'object') {
                        return (
                          <div key={serviceName} className="bg-neutral-dark/50 p-3 rounded-lg">
                            <p className="text-sm font-heading font-semibold text-neutral-light mb-2">
                              {getCashbackServiceDisplayName('proxycheap')}
                            </p>
                            <div className="space-y-1">
                              {value.basic !== undefined && (
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-neutral-medium font-body">
                                    {t('admin.systemConfig.cashback.proxycheap.basic')}:
                                  </span>
                                  <span className="text-xs font-heading font-semibold text-neutral-light">
                                    {(value.basic * 100).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                              {value.dedicated !== undefined && (
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-neutral-medium font-body">
                                    {t('admin.systemConfig.cashback.proxycheap.dedicated')}:
                                  </span>
                                  <span className="text-xs font-heading font-semibold text-neutral-light">
                                    {(value.dedicated * 100).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                              {value.premium !== undefined && (
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-neutral-medium font-body">
                                    {t('admin.systemConfig.cashback.proxycheap.premium')}:
                                  </span>
                                  <span className="text-xs font-heading font-semibold text-neutral-light">
                                    {(value.premium * 100).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      if (typeof value === 'number') {
                        return (
                          <div key={serviceName} className="flex justify-between items-center">
                            <span className="text-sm text-neutral-medium font-body">
                              {getCashbackServiceDisplayName(serviceName as CashbackServiceName)}:
                            </span>
                            <span className="text-sm font-heading font-semibold text-neutral-light">
                              {(value * 100).toFixed(2)}%
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </>
              )}
              <button
                onClick={handleStartEditCashback}
                className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('admin.systemConfig.cashback.edit')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.systemConfig.cashback.defaultPercentage')} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={cashbackForm.defaultPercentage}
                  onChange={(e) => setCashbackForm({ ...cashbackForm, defaultPercentage: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.systemConfig.cashback.percentageNote')}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-neutral-light mb-3">
                  {t('admin.systemConfig.cashback.services')}
                </h3>
                <div className="space-y-3">
                  {(['smspool', 'pvadeals', 'durianRcs', 'proxyline', 'warehouse'] as CashbackServiceName[]).map((serviceName) => (
                    <div key={serviceName}>
                      <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                        {getCashbackServiceDisplayName(serviceName)}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={cashbackForm.services[serviceName] || ''}
                        onChange={(e) =>
                          setCashbackForm({
                            ...cashbackForm,
                            services: { ...cashbackForm.services, [serviceName]: e.target.value },
                          })
                        }
                        placeholder="0.00"
                        className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                      />
                    </div>
                  ))}

                  {/* ProxyCheap */}
                  <div className="bg-neutral-dark/50 p-3 rounded-lg">
                    <p className="text-sm font-heading font-semibold text-neutral-light mb-2">
                      {getCashbackServiceDisplayName('proxycheap')}
                    </p>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-neutral-medium mb-1 font-body">
                          {t('admin.systemConfig.cashback.proxycheap.basic')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={cashbackForm.proxycheap.basic}
                          onChange={(e) =>
                            setCashbackForm({
                              ...cashbackForm,
                              proxycheap: { ...cashbackForm.proxycheap, basic: e.target.value },
                            })
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-medium mb-1 font-body">
                          {t('admin.systemConfig.cashback.proxycheap.dedicated')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={cashbackForm.proxycheap.dedicated}
                          onChange={(e) =>
                            setCashbackForm({
                              ...cashbackForm,
                              proxycheap: { ...cashbackForm.proxycheap, dedicated: e.target.value },
                            })
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-medium mb-1 font-body">
                          {t('admin.systemConfig.cashback.proxycheap.premium')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={cashbackForm.proxycheap.premium}
                          onChange={(e) =>
                            setCashbackForm({
                              ...cashbackForm,
                              proxycheap: { ...cashbackForm.proxycheap, premium: e.target.value },
                            })
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleUpdateCashback}
                  disabled={isUpdatingCashback}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {isUpdatingCashback ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => setIsEditingCashback(false)}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Webhook Secret Section */}
      <div className="mt-6 card-glass rounded-2xl shadow-2xl p-6">
        <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
          Webhook Signature Secret
        </h2>

        {!isEditingWebhookSecret ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                Status
              </label>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${webhookSecretData?.data.configured ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-lg font-heading font-bold text-neutral-light">
                  {webhookSecretData?.data.configured ? 'Configured' : 'Not Configured'}
                </span>
              </div>
              <p className="text-sm text-neutral-medium font-body mt-2">
                {webhookSecretData?.data.configured
                  ? 'Incoming webhooks require a valid X-Rocket-Signature header. Bypass is disabled.'
                  : 'No secret configured. Webhook signature verification is bypassed (token auth only).'}
              </p>
            </div>
            <button
              onClick={() => {
                setIsEditingWebhookSecret(true);
                setWebhookSecretInput('');
              }}
              className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
            >
              {webhookSecretData?.data.configured ? 'Change Secret' : 'Set Secret'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                Secret Key <span className="text-danger">*</span>
              </label>
              <input
                type="password"
                value={webhookSecretInput}
                onChange={(e) => setWebhookSecretInput(e.target.value)}
                placeholder="Enter webhook signing secret"
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono"
              />
              <p className="text-xs text-neutral-medium font-body mt-1">
                Leave empty and save to clear the secret and disable signature verification.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleUpdateWebhookSecret}
                disabled={isUpdatingWebhookSecret}
                className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isUpdatingWebhookSecret ? (
                  <>
                    <ButtonSpinner size="xs" />
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button
                onClick={() => {
                  setIsEditingWebhookSecret(false);
                  setWebhookSecretInput('');
                }}
                className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
